import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  centsToUSD,
  finance as financeApi,
  usdToCents,
  type FeeAdjustment,
  type FeeBasis,
  type FeeItem,
  type Fundraiser,
  type FundraiserSaleRow,
  type Payment,
  type PaymentMethod,
  type ScoutDues,
} from '../api';
import { useUnits } from '../units';

const BASIS_LABEL: Record<FeeBasis, string> = { per_scout: 'Per Scout', per_family: 'Per family' };

export function Finance() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole, councilCanView } = useUnits();
  const canManage = hasRole('ADMIN') || hasRole('PACK_COMMITTEE_FINANCE');
  const mayView = canManage || councilCanView('finance');

  const [fees, setFees] = useState<FeeItem[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [dues, setDues] = useState<ScoutDues[]>([]);
  const [fundraisers, setFundraisers] = useState<Fundraiser[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    financeApi
      .fees
      .list(unitId, showRetired)
      .then(setFees)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load fees'));
    financeApi.dues(unitId).then(setDues).catch(() => setDues([]));
    financeApi.fundraisers.list(unitId).then(setFundraisers).catch(() => setFundraisers([]));
  }, [unitId, showRetired]);

  useEffect(load, [load]);

  if (!mayView) return <Navigate to="/" replace />;

  const totalOutstanding = dues.reduce((s, d) => s + d.totalCents, 0);

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Finance</h1>
        {canManage && (
          <Link to={`/units/${unitId}/finance/report`} className="tile-link">
            Reporting &amp; export →
          </Link>
        )}
      </div>
      <p className="muted">{currentUnit?.name} · dues &amp; fees</p>
      {error && <p className="error">{error}</p>}

      <h2>Fee catalog</h2>
      {canManage && <NewFeeForm unitId={unitId} onCreated={load} />}
      <label className="filter">
        <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />{' '}
        Show retired
      </label>
      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Amount</th>
            <th>Basis</th>
            <th>Due</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {fees.map((f) => (
            <FeeRow key={f.id} unitId={unitId} fee={f} canManage={canManage} onChanged={load} />
          ))}
          {fees.length === 0 && (
            <tr>
              <td colSpan={canManage ? 5 : 4} className="muted">
                No fee items yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <h2>Balances</h2>
      <p className="muted small">
        {dues.length} Scout{dues.length === 1 ? '' : 's'} · {centsToUSD(totalOutstanding)} charged ·{' '}
        {centsToUSD(dues.reduce((s, d) => s + d.paidCents, 0))} paid ·{' '}
        <strong>{centsToUSD(dues.reduce((s, d) => s + d.balanceCents, 0))} outstanding</strong>
      </p>
      <table className="grid">
        <thead>
          <tr>
            <th>Scout</th>
            <th>Charged</th>
            <th>Paid</th>
            <th>Credits</th>
            <th>Balance</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {dues.map((d) => (
            <DuesRow key={d.scoutId} unitId={unitId} dues={d} fees={fees} canManage={canManage} onChanged={load} />
          ))}
          {dues.length === 0 && (
            <tr>
              <td colSpan={canManage ? 6 : 5} className="muted">
                No Scouts in scope.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <h2>Fundraisers</h2>
      {canManage && <NewFundraiserForm unitId={unitId} onCreated={load} />}
      {fundraisers.length === 0 ? (
        <p className="muted">No fundraisers yet.</p>
      ) : (
        fundraisers.map((f) => (
          <FundraiserCard key={f.id} unitId={unitId} fundraiser={f} onChanged={load} />
        ))
      )}
    </div>
  );
}

function NewFundraiserForm({ unitId, onCreated }: { unitId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [item, setItem] = useState('');
  const [price, setPrice] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await financeApi.fundraisers.create(unitId, {
        name,
        itemName: item || null,
        unitPriceCents: usdToCents(price),
        commissionRatePct: Math.round(Number(rate) || 0),
      });
      setName('');
      setItem('');
      setPrice('');
      setRate('');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create fundraiser');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit}>
      <input placeholder="Fundraiser name" value={name} onChange={(e) => setName(e.target.value)} required />
      <input placeholder="Item (e.g. Tin)" value={item} onChange={(e) => setItem(e.target.value)} />
      <input
        placeholder="$ / item"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        inputMode="decimal"
        required
        style={{ minWidth: 90 }}
      />
      <input
        placeholder="commission %"
        value={rate}
        onChange={(e) => setRate(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        required
        style={{ minWidth: 110 }}
      />
      <button type="submit" className="primary" disabled={busy || !name || !price || !rate}>
        Add fundraiser
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}

function FundraiserCard({
  unitId,
  fundraiser: f,
  onChanged,
}: {
  unitId: string;
  fundraiser: Fundraiser;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [sales, setSales] = useState<FundraiserSaleRow[]>([]);

  const reload = useCallback(() => {
    financeApi.fundraisers.sales(unitId, f.id).then((r) => setSales(r.sales)).catch(() => setSales([]));
  }, [unitId, f.id]);
  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  return (
    <div className="req-block">
      <button className="linklike" onClick={() => setOpen((o) => !o)}>
        <strong>{f.name}</strong>
      </button>{' '}
      <span className="muted small">
        {centsToUSD(f.unitPriceCents)}/{f.itemName ?? 'item'} · {f.commissionRatePct}% commission ·{' '}
        {f.itemsSold ?? 0} sold · {centsToUSD(f.grossCents ?? 0)} gross ·{' '}
        {centsToUSD(f.commissionCents ?? 0)} commission
      </span>
      {open && (
        <table className="grid" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Scout</th>
              <th>Qty sold</th>
              <th>Gross</th>
              <th>Commission</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((s) => (
              <SaleRow
                key={s.scoutId}
                unitId={unitId}
                fundraiserId={f.id}
                row={s}
                onSaved={() => {
                  reload();
                  onChanged();
                }}
              />
            ))}
            {sales.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No Scouts in scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SaleRow({
  unitId,
  fundraiserId,
  row,
  onSaved,
}: {
  unitId: string;
  fundraiserId: string;
  row: FundraiserSaleRow;
  onSaved: () => void;
}) {
  const [qty, setQty] = useState(String(row.quantity));
  const [busy, setBusy] = useState(false);
  const dirty = qty !== String(row.quantity);

  async function save() {
    setBusy(true);
    try {
      await financeApi.fundraisers.setSale(unitId, fundraiserId, {
        scoutId: row.scoutId,
        quantity: Math.max(0, Math.round(Number(qty) || 0)),
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        {row.lastName}, {row.firstName}
      </td>
      <td>
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          style={{ width: 70 }}
          onBlur={() => dirty && save()}
          disabled={busy}
        />
      </td>
      <td>{centsToUSD(row.grossCents)}</td>
      <td>{centsToUSD(row.commissionCents)}</td>
    </tr>
  );
}

function NewFeeForm({ unitId, onCreated }: { unitId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [basis, setBasis] = useState<FeeBasis>('per_scout');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await financeApi.fees.create(unitId, {
        name,
        amountCents: usdToCents(amount),
        basis,
        dueDate: dueDate || null,
      });
      setName('');
      setAmount('');
      setDueDate('');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add fee');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit}>
      <input placeholder="Fee name" value={name} onChange={(e) => setName(e.target.value)} required />
      <input
        placeholder="$ amount"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputMode="decimal"
        required
        style={{ minWidth: 100 }}
      />
      <select value={basis} onChange={(e) => setBasis(e.target.value as FeeBasis)}>
        <option value="per_scout">Per Scout</option>
        <option value="per_family">Per family</option>
      </select>
      <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} aria-label="Due date" />
      <button type="submit" className="primary" disabled={busy || !name || !amount}>
        Add fee
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}

function FeeRow({
  unitId,
  fee,
  canManage,
  onChanged,
}: {
  unitId: string;
  fee: FeeItem;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <tr>
      <td>
        {fee.name}
        {fee.retired && <span className="badge" style={{ marginLeft: 6 }}>retired</span>}
      </td>
      <td>{centsToUSD(fee.amountCents)}</td>
      <td>{BASIS_LABEL[fee.basis]}</td>
      <td className="muted">{fee.dueDate ?? '—'}</td>
      {canManage && (
        <td className="row-actions">
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await financeApi.fees.retire(unitId, fee.id, !fee.retired);
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
          >
            {fee.retired ? 'Restore' : 'Retire'}
          </button>
        </td>
      )}
    </tr>
  );
}

function DuesRow({
  unitId,
  dues,
  fees,
  canManage,
  onChanged,
}: {
  unitId: string;
  dues: ScoutDues;
  fees: FeeItem[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr>
        <td>
          <button className="linklike" onClick={() => setOpen((o) => !o)}>
            {dues.lastName}, {dues.firstName}
          </button>
        </td>
        <td>{centsToUSD(dues.totalCents)}</td>
        <td>{centsToUSD(dues.paidCents)}</td>
        <td className={dues.creditsCents ? '' : 'muted'}>
          {dues.creditsCents ? `−${centsToUSD(dues.creditsCents)}` : '—'}
        </td>
        <td>
          <strong className={dues.balanceCents > 0 ? 'error' : ''}>
            {centsToUSD(dues.balanceCents)}
          </strong>
        </td>
        {canManage && (
          <td className="row-actions">
            <button className="linklike" onClick={() => setOpen(true)}>
              Manage
            </button>
          </td>
        )}
      </tr>
      {open && (
        <tr>
          <td colSpan={canManage ? 6 : 5} className="adv-detail">
            {dues.creditLines.length > 0 && (
              <p className="muted small">
                Credits:{' '}
                {dues.creditLines.map((c) => `${c.name} ${centsToUSD(c.amountCents)}`).join(' · ')}
              </p>
            )}
            {dues.lines.length === 0 ? (
              <p className="muted">Nothing charged.</p>
            ) : (
              <table className="grid">
                <tbody>
                  {dues.lines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        {l.name}
                        {l.note && <span className="muted small"> ({l.note})</span>}
                      </td>
                      <td>{centsToUSD(l.baseCents)}</td>
                      <td className={l.adjustmentCents ? '' : 'muted'}>
                        {l.adjustmentCents ? centsToUSD(l.adjustmentCents) : '—'}
                      </td>
                      <td>
                        <strong>{centsToUSD(l.owedCents)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {canManage && (
              <>
                <h3>Payments</h3>
                <PaymentsPanel unitId={unitId} scoutId={dues.scoutId} onChanged={onChanged} />
                <h3>Adjustments</h3>
                <AdjustForm unitId={unitId} scoutId={dues.scoutId} fees={fees} onDone={onChanged} />
                <AdjustmentsList unitId={unitId} scoutId={dues.scoutId} onChanged={onChanged} />
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const METHODS: PaymentMethod[] = ['cash', 'check', 'other'];

function PaymentsPanel({
  unitId,
  scoutId,
  onChanged,
}: {
  unitId: string;
  scoutId: string;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<Payment[]>([]);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    financeApi.payments.list(unitId, scoutId).then(setRows).catch(() => setRows([]));
  }, [unitId, scoutId]);
  useEffect(reload, [reload]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await financeApi.payments.record(unitId, {
        scoutId,
        amountCents: usdToCents(amount),
        method,
        paidAt,
        reference: reference || null,
      });
      setAmount('');
      setReference('');
      reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record payment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <table className="grid">
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className={p.reversed ? 'muted' : ''}>
              <td>{p.paidAt}</td>
              <td>{centsToUSD(p.amountCents)}</td>
              <td>{p.method}</td>
              <td className="muted small">
                {p.reference}
                {p.reversed && ` · reversed${p.reversalReason ? `: ${p.reversalReason}` : ''}`}
              </td>
              <td className="row-actions">
                {!p.reversed && (
                  <button
                    onClick={async () => {
                      const reason = prompt('Reason for reversing this payment?');
                      if (!reason) return;
                      await financeApi.payments.reverse(unitId, p.id, reason);
                      reload();
                      onChanged();
                    }}
                  >
                    Reverse
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td className="muted">No payments recorded.</td>
            </tr>
          )}
        </tbody>
      </table>
      <form className="add-member" onSubmit={submit} style={{ marginTop: 8 }}>
        <input
          placeholder="$ amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          required
          style={{ minWidth: 100 }}
        />
        <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} aria-label="Paid on" />
        <input placeholder="Check # / ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        <button type="submit" className="primary" disabled={busy || !amount}>
          Record payment
        </button>
        {error && <span className="error">{error}</span>}
      </form>
    </>
  );
}

function AdjustmentsList({
  unitId,
  scoutId,
  onChanged,
}: {
  unitId: string;
  scoutId: string;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<FeeAdjustment[]>([]);
  const reload = useCallback(() => {
    financeApi.adjustments.list(unitId, scoutId).then(setRows).catch(() => setRows([]));
  }, [unitId, scoutId]);
  useEffect(reload, [reload]);

  if (rows.length === 0) return null;
  return (
    <table className="grid" style={{ marginTop: 8 }}>
      <tbody>
        {rows.map((a) => (
          <tr key={a.id}>
            <td>{a.feeItemName ?? 'General'}</td>
            <td>{centsToUSD(a.amountCents)}</td>
            <td className="muted small">{a.reason}</td>
            <td className="row-actions">
              <button
                onClick={async () => {
                  await financeApi.adjustments.remove(unitId, a.id);
                  reload();
                  onChanged();
                }}
              >
                Remove
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AdjustForm({
  unitId,
  scoutId,
  fees,
  onDone,
}: {
  unitId: string;
  scoutId: string;
  fees: FeeItem[];
  onDone: () => void;
}) {
  const [feeItemId, setFeeItemId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await financeApi.adjustments.add(unitId, {
        scoutId,
        feeItemId: feeItemId || null,
        amountCents: usdToCents(amount),
        reason,
      });
      setAmount('');
      setReason('');
      setFeeItemId('');
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save adjustment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit} style={{ marginTop: 8 }}>
      <select value={feeItemId} onChange={(e) => setFeeItemId(e.target.value)}>
        <option value="">General adjustment</option>
        {fees.filter((f) => !f.retired).map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </select>
      <input
        placeholder="$ (– for waiver)"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputMode="decimal"
        required
        style={{ minWidth: 120 }}
      />
      <input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
      <button type="submit" className="primary" disabled={busy || !amount || !reason}>
        Add adjustment
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}
